import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled, {css} from 'styled-components';

import {Handler, HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {useLayer} from '../../../../../../../libs/shared/ui/src/components/layers/layerContext';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import emptyParticipants from '../../../../../../assets/images/emptyParticipant70.svg';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {TeammateModel} from '../../../../../../models/api/teammateModel';
import {DropdownEmpty} from '../../../../../common/dropdowns/dropdownEmpty';
import {DropdownVirtualList} from '../../../../../common/dropdowns/dropdownVirtualList';
import {InviteDropdownTrackerItem} from './inviteDropdownTrackerItem';
import {VisualConversationTrackerModel} from './inviteHelpers';

/*
 * Constants.
 */

const dropdownVerticalPadding = 300;
const notSubscribedHeaderHeight = 37;
const subscribedEmptyTextHeight = 20;
const defaultDropdownRowHeight = 30;

/*
 * Props.
 */

interface InviteDropdownTrackerContentProps {
  userId: number;
  conversation: ConversationModel;
  visualTrackers: ReadonlyArray<VisualConversationTrackerModel>;
  sortedSubscribersInfo: SortedSubscribersInfo;
  onRequestClose?: Handler;
  isUserVisitor: boolean;
  onSubscribeClick: Handler;
  onUnsubscribeClick: Handler;
  onRemoveTeammateClick: HandlerOf<TeammateModel>;
}

/*
 * Types.
 */

export interface SortedSubscribersInfo {
  subscribers: ReadonlyArray<VisualConversationTrackerModel>;
  nonSubscribers: ReadonlyArray<VisualConversationTrackerModel>;
}

enum InviteDropdownItemTypesEnum {
  SUBSCRIBED_HEADER = 'subscribed_header',
  NOT_SUBSCRIBED_HEADER = 'not_subscribed_header',
  SUBSCRIBED_EMPTY_TEXT = 'subscribed_empty_text',
  SUBSCRIBER = 'subscriber',
  NON_SUBSCRIBER = 'non_subscriber',
}

interface SubscribedHeader {
  type: InviteDropdownItemTypesEnum.SUBSCRIBED_HEADER;
}

interface NotSubscribedHeader {
  type: InviteDropdownItemTypesEnum.NOT_SUBSCRIBED_HEADER;
}

interface SubscribedEmptyText {
  type: InviteDropdownItemTypesEnum.SUBSCRIBED_EMPTY_TEXT;
}

interface Subscriber {
  type: InviteDropdownItemTypesEnum.SUBSCRIBER;
  visualTracker: VisualConversationTrackerModel;
}

interface NonSubscriber {
  type: InviteDropdownItemTypesEnum.NON_SUBSCRIBER;
  visualTracker: VisualConversationTrackerModel;
}

type InviteDropdownItem =
  | SubscribedHeader
  | NotSubscribedHeader
  | SubscribedEmptyText
  | Subscriber
  | NonSubscriber;

/*
 * Style.
 */

const StyledListDiv = styled.div`
  height: 248px;
  ${center()};
`;

const StyledSubscribedSubheader = styled.div`
  padding: 7px 0 7px 12px;

  ${addSubheaderStyles()}
`;

const StyledNotSubscribedSubheader = styled.div`
  padding: 15px 0 7px 12px;

  ${addSubheaderStyles()}
`;

function addSubheaderStyles() {
  return css`
    font-weight: ${fontWeights.semibold};
    font-size: ${fontSizes.verySmall};
    color: ${(p) => p.theme.greys.shade60};
  `;
}

const StyledEmptyStateText = styled.div`
  padding: 2px 0 0 12px;

  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const InviteDropdownTrackersContent: FC<InviteDropdownTrackerContentProps> = (props) => {
  const {onRemoveTeammateClick, sortedSubscribersInfo} = props;

  // Window innerHeight is used to calculate maximum height of dropdown.
  const {window} = useLayer();
  const [windowHeight, setWindowHeight] = useState<number>(window.innerHeight);

  useEffect(() => {
    function handleResize() {
      setWindowHeight(window.innerHeight);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [setWindowHeight, window]);

  const dropdownItems = computeDropdownItems(
    sortedSubscribersInfo.subscribers,
    sortedSubscribersInfo.nonSubscribers,
  );

  // This is only possible if seenBy doesn't load and it's a team conversation.
  // Otherwise, the user herself should always be in dropdownItems.
  if (dropdownItems.length === 0) {
    return renderEmptyState();
  }

  return (
    <DropdownVirtualList
      width={320}
      minHeight={undefined}
      maxHeight={windowHeight - dropdownVerticalPadding}
      rowCount={dropdownItems.length}
      renderRow={(index) => renderRow(index, dropdownItems, onRemoveTeammateClick, props)}
      getRowHeight={(index) => getRowHeight(index, dropdownItems)}
    />
  );
};

/*
 * Helpers.
 */

/** Renders dropdown empty state. */
function renderEmptyState() {
  return (
    <StyledListDiv>
      <DropdownEmpty imageSource={emptyParticipants}>
        <Trans id="invite-noparticipants">No participants</Trans>
      </DropdownEmpty>
    </StyledListDiv>
  );
}

/** Renders dropdown row. */
function renderRow(
  index: number,
  dropdownItems: ReadonlyArray<InviteDropdownItem>,
  onRemoveTeammateClick: HandlerOf<TeammateModel>,
  props: InviteDropdownTrackerContentProps,
) {
  const {isUserVisitor, conversation, onSubscribeClick, onUnsubscribeClick} = props;
  const item = dropdownItems[index];

  const shouldShowSubscriptionButtons = computeShouldShowSubscriptionButtons(item);

  switch (item.type) {
    case InviteDropdownItemTypesEnum.SUBSCRIBED_HEADER:
      return (
        <StyledSubscribedSubheader>
          <Trans id="invitedropdown-subscribed">Subscribed</Trans>
        </StyledSubscribedSubheader>
      );
    case InviteDropdownItemTypesEnum.NOT_SUBSCRIBED_HEADER:
      return (
        <StyledNotSubscribedSubheader>
          <Trans id="invitedropdown-notsubscribed">Not subscribed</Trans>
        </StyledNotSubscribedSubheader>
      );
    case InviteDropdownItemTypesEnum.SUBSCRIBED_EMPTY_TEXT:
      return <StyledEmptyStateText>None</StyledEmptyStateText>;
    case InviteDropdownItemTypesEnum.SUBSCRIBER:
      return (
        <InviteDropdownTrackerItem
          tracker={item.visualTracker}
          index={index}
          isUserVisitor={isUserVisitor}
          onRemoveClick={() => onRemoveTeammateClick(item.visualTracker.teammate)}
          onSubscribeClick={shouldShowSubscriptionButtons ? onSubscribeClick : undefined}
          onUnsubscribeClick={shouldShowSubscriptionButtons ? onUnsubscribeClick : undefined}
          isUserAssignee={conversation.assigneeId === item.visualTracker.teammate.id}
        />
      );
    case InviteDropdownItemTypesEnum.NON_SUBSCRIBER:
      return (
        <InviteDropdownTrackerItem
          tracker={item.visualTracker}
          index={index}
          isUserVisitor={isUserVisitor}
          onRemoveClick={() => onRemoveTeammateClick(item.visualTracker.teammate)}
          onSubscribeClick={shouldShowSubscriptionButtons ? onSubscribeClick : undefined}
          onUnsubscribeClick={shouldShowSubscriptionButtons ? onUnsubscribeClick : undefined}
          isFaded
        />
      );

    default:
      return null;
  }
}

/** Computes a row height. */
function getRowHeight(index: number, dropdownItems: ReadonlyArray<InviteDropdownItem>) {
  const item = dropdownItems[index];

  switch (item.type) {
    case InviteDropdownItemTypesEnum.NOT_SUBSCRIBED_HEADER:
      return notSubscribedHeaderHeight;
    case InviteDropdownItemTypesEnum.SUBSCRIBED_EMPTY_TEXT:
      return subscribedEmptyTextHeight;
    default:
      return defaultDropdownRowHeight;
  }
}

/** Computes whether to show subscription buttons. */
function computeShouldShowSubscriptionButtons(inviteDropdownItem: InviteDropdownItem) {
  if (
    inviteDropdownItem.type !== InviteDropdownItemTypesEnum.SUBSCRIBER &&
    inviteDropdownItem.type !== InviteDropdownItemTypesEnum.NON_SUBSCRIBER
  ) {
    return false;
  }

  // only the user can change their own subscription status.
  return inviteDropdownItem.visualTracker.isUserOrDelegatedTeammate;
}

function computeDropdownItems(
  sortedSubscribers: ReadonlyArray<VisualConversationTrackerModel>,
  sortedNonSubscribers: ReadonlyArray<VisualConversationTrackerModel>,
): ReadonlyArray<InviteDropdownItem> {
  // show empty text when there are non subscribers but no subscribers
  const subscribedEmptyText: InviteDropdownItem = {
    type: InviteDropdownItemTypesEnum.SUBSCRIBED_EMPTY_TEXT,
  };

  const subscriberItems = sortedSubscribers.map(
    (visualTracker): Subscriber => ({visualTracker, type: InviteDropdownItemTypesEnum.SUBSCRIBER}),
  );

  const subscriberContent = subscriberItems.length > 0 ? subscriberItems : [subscribedEmptyText];

  // only show headers when there are non subscribers for the conversation
  const shouldShowHeaders = sortedNonSubscribers.length > 0;

  if (!shouldShowHeaders) {
    return subscriberItems;
  }

  // subscribed header
  const subscribedHeader: InviteDropdownItem = {
    type: InviteDropdownItemTypesEnum.SUBSCRIBED_HEADER,
  };

  // not subscribed header
  const notSubscribedHeader: InviteDropdownItem = {
    type: InviteDropdownItemTypesEnum.NOT_SUBSCRIBED_HEADER,
  };

  const nonSubscriberItems = sortedNonSubscribers.map(
    (visualTracker): NonSubscriber => ({visualTracker, type: InviteDropdownItemTypesEnum.NON_SUBSCRIBER}),
  );

  return [subscribedHeader, ...subscriberContent, notSubscribedHeader, ...nonSubscriberItems];
}
